import { supabase } from '../lib/supabaseClient';

const OPEN_CASE_STATUSES = ['pending_approval', 'approved_not_started', 'job_posting', 'interview_scheduled', 'waiting_interview_result', 'offer_sent', 'accepted_waiting_start'];
const CLOSED_CASE_STATUSES = ['started', 'cancelled_on_hold'];
const STATUS_LABELS = {
  pending_approval: 'Pending Approval',
  approved_not_started: 'Approved / Not Started',
  job_posting: 'Job Posting',
  interview_scheduled: 'Interview Scheduled',
  waiting_interview_result: 'Waiting for Result',
  offer_sent: 'Offer Sent',
  accepted_waiting_start: 'Accepted / Waiting',
  started: 'Started',
  cancelled_on_hold: 'Cancelled',
};

export function createSupabaseApi(user) {
  if (!supabase) {
    throw new Error('Supabase is not configured');
  }

  return {
    get: (url, config = {}) => routeGet(url, config.params || {}, user),
    post: (url, data = {}) => routePost(url, data, user),
    put: (url, data = {}) => routePut(url, data, user),
    delete: (url) => routeDelete(url, user),
    exportDashboardCSV: async () => exportDashboardCsv(user),
    exportCandidatesCSV: async () => exportCandidatesCsv(user),
  };
}

async function routeGet(url, params, user) {
  if (url === '/dashboard/summary') return { data: await getDashboardSummary(params) };
  if (url === '/dashboard/by-status') return { data: await getDashboardByStatus(params) };
  if (url === '/dashboard/by-aging') return { data: await getDashboardByAging(params) };
  if (url === '/dashboard/by-project') return { data: await getDashboardByProject(params) };
  if (url === '/dashboard/by-position') return { data: await getDashboardByPosition(params) };
  if (url === '/dashboard/drill-down') return { data: await getDashboardDrillDown(params) };
  if (url === '/dashboard/candidate-pool') return { data: await getCandidatePool(params) };
  if (url === '/sync/google-sheet/status') return { data: await getSyncStatus() };
  if (url === '/projects') return { data: await getProjects() };
  if (url.startsWith('/projects/')) return { data: await getProjectById(url.split('/')[2]) };
  if (url === '/positions') return { data: await getPositions() };
  if (url === '/lookups/areas') return { data: await getAreas() };
  if (url === '/lookups/work-zones') return { data: await getWorkZones() };
  if (url === '/lookups/statuses') return { data: Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label })) };
  if (url === '/headcount/required') return { data: await getRequiredHeadcount(params) };
  if (url === '/headcount/actual') return { data: await getActualHeadcount(params) };
  if (url === '/manpower-requests') return { data: await getManpowerRequests(params) };
  if (url === '/recruitment-cases') return { data: await getRecruitmentCases(params) };
  if (url.startsWith('/recruitment-cases/')) return { data: await getRecruitmentCaseById(url.split('/')[2]) };
  if (url === '/candidates') return { data: await getCandidates(params) };
  if (url.startsWith('/candidates/')) return { data: await getCandidateById(url.split('/')[2]) };
  throw new Error(`GET ${url} is not implemented`);
}

async function routePost(url, data, user) {
  if (url === '/sync/google-sheet') throw new Error('Google Sheet sync is not available in Cloudflare frontend-only mode');
  if (url === '/manpower-requests') return { data: await createManpowerRequest(data, user) };
  if (url === '/candidates') return { data: await createCandidate(data, user) };
  if (url === '/recruitment-cases') return { data: await createRecruitmentCase(data, user) };
  throw new Error(`POST ${url} is not implemented`);
}

async function routePut(url, data, user) {
  if (url.startsWith('/manpower-requests/') && url.endsWith('/approve')) {
    return { data: await approveManpowerRequest(url.split('/')[2], user) };
  }
  if (url.startsWith('/recruitment-cases/')) return { data: await updateRecruitmentCase(url.split('/')[2], data, user) };
  if (url.startsWith('/candidates/') && url.endsWith('/pass-status')) return { data: await updateCandidatePass(url.split('/')[2], data, user) };
  if (url.startsWith('/candidates/')) return { data: await updateCandidate(url.split('/')[2], data, user) };
  throw new Error(`PUT ${url} is not implemented`);
}

async function routeDelete(url) {
  if (url.startsWith('/candidates/')) return { data: await removeCandidate(url.split('/')[2]) };
  throw new Error(`DELETE ${url} is not implemented`);
}

async function getProjects() {
  const { data, error } = await supabase.from('projects').select('*').order('name');
  if (error) throw error;
  return data || [];
}

async function getProjectById(id) {
  const { data, error } = await supabase.from('projects').select('*').eq('id', id).single();
  if (error) throw error;
  return data;
}

async function getPositions() {
  const { data, error } = await supabase.from('positions').select('*').order('name');
  if (error) throw error;
  return data || [];
}

async function getAreas() {
  const projects = await getProjects();
  return [...new Set(projects.map((item) => item.area).filter(Boolean))].sort();
}

async function getWorkZones() {
  const { data, error } = await supabase.from('candidates').select('preferred_work_zone');
  if (error) throw error;
  return [...new Set((data || []).map((item) => item.preferred_work_zone).filter(Boolean))].sort();
}

async function getRequiredHeadcount(params = {}) {
  let query = supabase.from('required_headcount').select('*');
  if (params.project_id) query = query.eq('project_id', params.project_id);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

async function getActualHeadcount(params = {}) {
  let query = supabase.from('actual_headcount').select('*');
  if (params.project_id) query = query.eq('project_id', params.project_id);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

async function getManpowerRequests(params = {}) {
  let query = supabase.from('manpower_requests').select('*').order('created_at', { ascending: false });
  if (params.project_id) query = query.eq('project_id', params.project_id);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

async function createManpowerRequest(payload, user) {
  const row = {
    id: `mr-${crypto.randomUUID()}`,
    ...payload,
    request_qty: Number(payload.request_qty || 1),
    request_date: new Date().toISOString(),
    requested_by_name: user?.name || null,
    status: 'pending',
    source: 'app',
    created_by_operator_code: user?.operatorCode || null,
    updated_by_operator_code: user?.operatorCode || null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await supabase.from('manpower_requests').insert(row).select().single();
  if (error) throw error;
  return data;
}

async function approveManpowerRequest(id, user) {
  const approvedDate = new Date().toISOString();
  const { data, error } = await supabase.from('manpower_requests').update({
    status: 'approved',
    approved_date: approvedDate,
    updated_at: approvedDate,
    updated_by_operator_code: user?.operatorCode || null,
  }).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

async function getRecruitmentCases(params = {}) {
  let query = supabase.from('recruitment_cases').select('*').order('created_at', { ascending: false });
  if (params.project_id) query = query.eq('project_id', params.project_id);
  if (params.status) query = query.eq('status', params.status);
  const { data, error } = await query;
  if (error) throw error;
  const items = (data || []).map(enrichCaseAging);
  return items;
}

async function getRecruitmentCaseById(id) {
  const { data, error } = await supabase.from('recruitment_cases').select('*').eq('id', id).single();
  if (error) throw error;
  return enrichCaseAging(data);
}

async function createRecruitmentCase(payload, user) {
  const now = new Date().toISOString();
  const row = {
    id: `rc-${crypto.randomUUID()}`,
    qty: Number(payload.qty || 1),
    ...payload,
    source: 'app',
    created_by_operator_code: user?.operatorCode || null,
    updated_by_operator_code: user?.operatorCode || null,
    created_at: now,
    updated_at: now,
  };
  const { data, error } = await supabase.from('recruitment_cases').insert(row).select().single();
  if (error) throw error;
  return enrichCaseAging(data);
}

async function updateRecruitmentCase(id, payload, user) {
  const next = {
    ...payload,
    updated_at: new Date().toISOString(),
    updated_by_operator_code: user?.operatorCode || null,
  };
  const { data, error } = await supabase.from('recruitment_cases').update(next).eq('id', id).select().single();
  if (error) throw error;
  return enrichCaseAging(data);
}

async function getCandidates(params = {}) {
  let query = supabase.from('candidates').select('*').order('created_at', { ascending: false });
  if (params.position_id) query = query.eq('position_id', params.position_id);
  if (params.preferred_work_zone) query = query.eq('preferred_work_zone', params.preferred_work_zone);
  if (params.mapping_status) query = query.eq('mapping_status', params.mapping_status);
  const { data, error } = await query;
  if (error) throw error;
  let rows = data || [];
  if (params.pass_status === 'passed') rows = rows.filter((item) => item.pass_status?.passed);
  if (params.pass_status === 'not_passed') rows = rows.filter((item) => !item.pass_status?.passed);
  return rows;
}

async function getCandidateById(id) {
  const { data, error } = await supabase.from('candidates').select('*').eq('id', id).single();
  if (error) throw error;
  return data;
}

async function createCandidate(payload, user) {
  const now = new Date().toISOString();
  const row = normalizeCandidatePayload({
    ...payload,
    id: `cand-${crypto.randomUUID()}`,
    source: 'app',
    created_by_operator_code: user?.operatorCode || null,
    updated_by_operator_code: user?.operatorCode || null,
    created_at: now,
    updated_at: now,
  });
  const { data, error } = await supabase.from('candidates').insert(row).select().single();
  if (error) throw error;
  return data;
}

async function updateCandidate(id, payload, user) {
  const row = normalizeCandidatePayload({
    ...payload,
    updated_at: new Date().toISOString(),
    updated_by_operator_code: user?.operatorCode || null,
  });
  const { data, error } = await supabase.from('candidates').update(row).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

async function updateCandidatePass(id, payload, user) {
  const pass_status = {
    passed: Boolean(payload.passed),
    method: 'manual',
    checked_by_operator_code: user?.operatorCode || null,
    checked_by_name: user?.name || null,
    checked_at: new Date().toISOString(),
  };
  const { data, error } = await supabase.from('candidates').update({
    pass_status,
    updated_at: new Date().toISOString(),
    updated_by_operator_code: user?.operatorCode || null,
  }).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

async function removeCandidate(id) {
  const { error } = await supabase.from('candidates').delete().eq('id', id);
  if (error) throw error;
  return { success: true };
}

async function getDashboardSummary(params = {}) {
  const { requiredRows, actualRows, cases, candidates } = await fetchDashboardBase(params);
  const total_required = requiredRows.reduce((sum, item) => sum + Number(item.required_qty || 0), 0);
  const total_actual = actualRows.reduce((sum, item) => sum + Number(item.actual_qty || 0), 0);
  const total_missing = Math.max(total_required - total_actual, 0);
  const open_cases = cases.length;
  const open_case_qty = cases.reduce((sum, item) => sum + Number(item.qty || 0), 0);
  const cases_over_30_days = cases.filter((item) => item.aging_days > 30).length;
  return {
    total_required,
    total_actual,
    total_missing,
    open_cases,
    open_case_qty,
    cases_over_30_days,
    has_gap_without_cases: total_missing > open_case_qty,
    gap_without_cases: Math.max(total_missing - open_case_qty, 0),
    pool_total: candidates.length,
    pool_passed: candidates.filter((item) => item.pass_status?.passed).length,
  };
}

async function getDashboardByStatus(params = {}) {
  const { cases } = await fetchDashboardBase(params);
  const grouped = new Map();
  for (const item of cases) {
    const current = grouped.get(item.status) || { status: item.status, label: STATUS_LABELS[item.status] || item.status, count: 0, qty: 0 };
    current.count += 1;
    current.qty += Number(item.qty || 0);
    grouped.set(item.status, current);
  }
  return [...grouped.values()];
}

async function getDashboardByAging(params = {}) {
  const { cases } = await fetchDashboardBase(params);
  const buckets = { '0-7': 0, '8-14': 0, '15-30': 0, '>30': 0 };
  for (const item of cases) {
    buckets[item.aging_group] += Number(item.qty || 0);
  }
  return Object.entries(buckets).map(([group, qty]) => ({ group, qty }));
}

async function getDashboardByProject(params = {}) {
  const [projects, requiredRows, actualRows, cases] = await Promise.all([
    getProjects(),
    getRequiredHeadcount({}),
    getActualHeadcount({}),
    getRecruitmentCases({}),
  ]);

  const filteredProjects = projects.filter((item) => !params.area || item.area === params.area);
  return filteredProjects.map((project) => {
    const required = requiredRows.filter((item) => item.project_id === project.id).reduce((sum, item) => sum + Number(item.required_qty || 0), 0);
    const actual = actualRows.filter((item) => item.project_id === project.id).reduce((sum, item) => sum + Number(item.actual_qty || 0), 0);
    const openCases = cases.filter((item) => item.project_id === project.id).length;
    return {
      project_id: project.id,
      project_name: project.name,
      area: project.area,
      required,
      actual,
      gap: Math.max(required - actual, 0),
      open_cases: openCases,
    };
  });
}

async function getDashboardByPosition(params = {}) {
  const [positions, requiredRows, actualRows] = await Promise.all([
    getPositions(),
    getRequiredHeadcount(params.project_id ? { project_id: params.project_id } : {}),
    getActualHeadcount(params.project_id ? { project_id: params.project_id } : {}),
  ]);
  return positions.map((position) => {
    const required = requiredRows.filter((item) => item.position_id === position.id).reduce((sum, item) => sum + Number(item.required_qty || 0), 0);
    const actual = actualRows.filter((item) => item.position_id === position.id).reduce((sum, item) => sum + Number(item.actual_qty || 0), 0);
    return {
      position_id: position.id,
      position_name: position.name,
      required,
      actual,
      gap: Math.max(required - actual, 0),
    };
  }).filter((item) => item.required > 0 || item.actual > 0);
}

async function getDashboardDrillDown(params = {}) {
  const [cases, projects, positions] = await Promise.all([getRecruitmentCases({}), getProjects(), getPositions()]);
  const projectMap = Object.fromEntries(projects.map((item) => [item.id, item.name]));
  const positionMap = Object.fromEntries(positions.map((item) => [item.id, item.name]));
  return cases.filter((item) => {
    if (params.status && item.status !== params.status) return false;
    if (params.aging_group && item.aging_group !== params.aging_group) return false;
    if (params.project_id && item.project_id !== params.project_id) return false;
    if (params.position_id && item.position_id !== params.position_id) return false;
    return true;
  }).map((item) => ({
    case_id: item.id,
    request_id: item.request_id,
    project_name: projectMap[item.project_id] || 'Unknown',
    project_id: item.project_id,
    position_name: positionMap[item.position_id] || 'Unknown',
    position_id: item.position_id,
    qty: item.qty,
    status: item.status,
    status_label: STATUS_LABELS[item.status] || item.status,
    aging_days: item.aging_days,
    aging_group: item.aging_group,
    approved_date: item.approved_date,
    start_date: item.start_date,
    notes: item.notes,
    created_at: item.created_at,
  }));
}

async function getCandidatePool(params = {}) {
  let candidates = await getCandidates({});
  const positions = await getPositions();
  const posMap = Object.fromEntries(positions.map((item) => [item.id, item.name]));
  if (params.position_id) candidates = candidates.filter((item) => item.position_id === params.position_id);
  const byPosition = new Map();
  const byZone = new Map();
  for (const item of candidates) {
    const posId = item.position_id || '';
    const zone = item.preferred_work_zone || 'Unknown';
    if (posId) {
      const current = byPosition.get(posId) || { position_id: posId, position_name: posMap[posId] || 'Unknown', total: 0, passed: 0 };
      current.total += 1;
      if (item.pass_status?.passed) current.passed += 1;
      byPosition.set(posId, current);
    }
    const currentZone = byZone.get(zone) || { zone, total: 0, passed: 0 };
    currentZone.total += 1;
    if (item.pass_status?.passed) currentZone.passed += 1;
    byZone.set(zone, currentZone);
  }
  return {
    total: candidates.length,
    passed: candidates.filter((item) => item.pass_status?.passed).length,
    by_position: [...byPosition.values()],
    by_zone: [...byZone.values()],
  };
}

async function getSyncStatus() {
  const { data, error } = await supabase.from('system_meta').select('*').eq('key', 'google_sheet_initial_import').maybeSingle();
  if (error) throw error;
  const value = data?.value || {};
  return {
    synced: Boolean(data),
    synced_at: value.imported_at || null,
    spreadsheet_url: value.spreadsheet_url || null,
    tracking_sheet: value.tracking_sheet || null,
    application_sheet: value.application_sheet || null,
  };
}

async function fetchDashboardBase(params = {}) {
  const [requiredRows, actualRows, cases, candidates] = await Promise.all([
    getRequiredHeadcount(params.project_id ? { project_id: params.project_id } : {}),
    getActualHeadcount(params.project_id ? { project_id: params.project_id } : {}),
    getRecruitmentCases({ project_id: params.project_id }),
    getCandidates(params.position_id ? { position_id: params.position_id } : {}),
  ]);
  let filteredRequired = requiredRows;
  let filteredActual = actualRows;
  let filteredCases = cases.filter((item) => OPEN_CASE_STATUSES.includes(item.status));
  if (params.position_id) {
    filteredRequired = filteredRequired.filter((item) => item.position_id === params.position_id);
    filteredActual = filteredActual.filter((item) => item.position_id === params.position_id);
    filteredCases = filteredCases.filter((item) => item.position_id === params.position_id);
  }
  if (params.area) {
    const projects = await getProjects();
    const projectIds = new Set(projects.filter((item) => item.area === params.area).map((item) => item.id));
    filteredRequired = filteredRequired.filter((item) => projectIds.has(item.project_id));
    filteredActual = filteredActual.filter((item) => projectIds.has(item.project_id));
    filteredCases = filteredCases.filter((item) => projectIds.has(item.project_id));
  }
  return { requiredRows: filteredRequired, actualRows: filteredActual, cases: filteredCases, candidates };
}

function enrichCaseAging(item) {
  const sourceDate = item.approved_date || item.created_at || new Date().toISOString();
  const aging_days = Math.max(0, Math.floor((Date.now() - new Date(sourceDate).getTime()) / 86400000));
  let aging_group = '>30';
  if (aging_days <= 7) aging_group = '0-7';
  else if (aging_days <= 14) aging_group = '8-14';
  else if (aging_days <= 30) aging_group = '15-30';
  return {
    ...item,
    aging_days,
    aging_group,
    status_history: item.status_history || [{ status: item.status, changed_at: item.updated_at || item.created_at, notes: item.notes || '' }],
  };
}

function normalizeCandidatePayload(payload) {
  return {
    ...payload,
    age: payload.age ? Number(payload.age) : null,
    last_salary: payload.last_salary ? Number(payload.last_salary) : null,
    expected_salary: payload.expected_salary ? Number(payload.expected_salary) : null,
    scores: payload.scores || {},
    pass_status: payload.pass_status || payload.passStatus || payload.pass_status || {},
  };
}

async function exportDashboardCsv() {
  const rows = await getDashboardByProject({});
  const csv = [['Project', 'Area', 'Required', 'Actual', 'Gap', 'Open Cases'], ...rows.map((item) => [item.project_name, item.area || '', item.required, item.actual, item.gap, item.open_cases])]
    .map((row) => row.map(csvEscape).join(','))
    .join('\n');
  downloadCsv(csv, 'dashboard_export.csv');
}

async function exportCandidatesCsv() {
  const rows = await getCandidates({});
  const csv = [['Name', 'Position ID', 'Email', 'Phone', 'Zone', 'Mapping Status', 'Offer Status'], ...rows.map((item) => [`${item.prefix || ''} ${item.first_name || ''} ${item.last_name || ''}`.trim(), item.position_id || '', item.email || '', item.phone || '', item.preferred_work_zone || '', item.mapping_status || '', item.offer_status || ''])]
    .map((row) => row.map(csvEscape).join(','))
    .join('\n');
  downloadCsv(csv, 'candidates_export.csv');
}

function downloadCsv(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function csvEscape(value) {
  const text = String(value ?? '');
  return `"${text.replaceAll('"', '""')}"`;
}
